/**
 * TechAlertPreferencesCard — self-service per-category opt-in for infra
 * alerts (Wallet, CRM, AI Translation, Backup Cloud, Infrastruttura).
 *
 * Fabrizio CR 01/07/2026 — Before this widget, ALL infra alerts were
 * broadcast to every user with role ``admin`` OR ``it``, which meant
 * LoJack business admins (Zorzutti, Minoia, Capelli) received Preview
 * environment alerts they never asked for. Now each admin can pick
 * exactly which categories they want to be paged for, from their own
 * dashboard. Backend endpoint: ``GET/PATCH /api/auth/me/tech-alert-preferences``.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, ShieldCheck, Info } from "lucide-react";
import { API } from "../../config";
import { useAuth } from "../../context/AuthContext";

// Emoji-less category icons + short helper labels for the row layout.
// Kept in-component (no external map) so the widget is a single file
// and easy to lift into IT Portal / Salesforce Portal later.
const CATEGORY_META = {
  wallet:  { title: "Wallet",        hint: "APN cert, Google Wallet, scadenza .p12" },
  crm:     { title: "CRM",           hint: "Salesforce webhook e connessione" },
  ai:      { title: "AI Translation", hint: "Backend LLM (traduzioni PDF, helper admin)" },
  storage: { title: "Backup Cloud",  hint: "Server dedicati TelcaVoIP — Regione Europa" },
  infra:   { title: "Infrastruttura", hint: "MongoDB, LibreOffice, container health" },
};

export const TechAlertPreferencesCard = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // category being toggled
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/auth/me/tech-alert-preferences`,
          getAuthHeader(),
        );
        if (!mounted) return;
        setPrefs(res.data.preferences || {});
        setCategories(res.data.categories || Object.keys(res.data.preferences || {}));
      } catch (e) {
        if (mounted) toast.error("Errore caricamento preferenze alert tecnici");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async (cat, next) => {
    setSaving(cat);
    const prev = prefs;
    // Optimistic update
    setPrefs({ ...prev, [cat]: next });
    try {
      const res = await axios.patch(
        `${API}/auth/me/tech-alert-preferences`,
        { [cat]: next },
        getAuthHeader(),
      );
      setPrefs(res.data.preferences);
      toast.success(next
        ? `Alert "${CATEGORY_META[cat]?.title || cat}" attivati`
        : `Alert "${CATEGORY_META[cat]?.title || cat}" disattivati`);
    } catch (e) {
      setPrefs(prev); // rollback
      toast.error(e.response?.data?.detail || "Errore salvataggio preferenza");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className="wallet-card"
      data-testid="tech-alert-preferences-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lojack-blue-500/15 border border-lojack-blue-500/30 flex items-center justify-center">
            <Bell size={18} className="text-lojack-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base flex items-center gap-2">
              Notifiche Tecniche
              <ShieldCheck size={14} className="text-lojack-blue-400" title="Preferenze personali — solo tu ricevi le mail selezionate" />
            </h3>
            <p className="text-gray-400 text-xs mt-0.5">
              Scegli quali alert infrastrutturali vuoi ricevere via email.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[#0d0d0d] border border-gray-800 rounded-xl p-3 mb-3 flex items-start gap-2">
        <Info size={14} className="text-gray-500 shrink-0 mt-0.5" />
        <p className="text-gray-500 text-xs leading-relaxed">
          Queste sono le tue preferenze personali per <strong className="text-gray-300">{user?.email}</strong>.
          Gli alert vengono inviati solo se una delle categorie che hai attivato entra in stato di errore o warning.
          Puoi cambiare in qualsiasi momento senza notifiche a nessuno.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="spinner"></div>
        </div>
      ) : !prefs ? (
        <p className="text-gray-500 text-sm text-center py-4">Preferenze non disponibili</p>
      ) : (
        <div className="space-y-2" data-testid="tech-alert-preferences-list">
          {categories.map((cat) => {
            const meta = CATEGORY_META[cat] || { title: cat, hint: "" };
            const isOn = !!prefs[cat];
            const isSaving = saving === cat;
            return (
              <div
                key={cat}
                className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border transition-colors ${
                  isOn
                    ? "bg-lojack-blue-500/5 border-lojack-blue-500/30"
                    : "bg-[#0d0d0d] border-gray-800"
                }`}
                data-testid={`tech-alert-row-${cat}`}
              >
                <div className="min-w-0 flex-1">
                  <p className={`font-medium text-sm ${isOn ? "text-white" : "text-gray-300"}`}>
                    {meta.title}
                  </p>
                  <p className="text-gray-500 text-xs mt-0.5">{meta.hint}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggle(cat, !isOn)}
                  disabled={isSaving}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-lojack-blue-400 focus:ring-offset-2 focus:ring-offset-[#111] ${
                    isOn ? "bg-lojack-blue-500" : "bg-gray-700"
                  } ${isSaving ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                  role="switch"
                  aria-checked={isOn}
                  aria-label={`Alert ${meta.title}: ${isOn ? "attivi" : "disattivi"}`}
                  data-testid={`tech-alert-toggle-${cat}`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                      isOn ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TechAlertPreferencesCard;
